/**
 * annotationsFromSections — translate annotation-typed Sections in the
 * structured paragraph-list into the legacy `AnnotationData` shape so
 * the existing AnnotationHighlight (TipTap inline-decoration) and
 * AnnotationTooltip (hover card) can render them transparently.
 *
 * The migration path is end-to-end: agents and the UI now write
 * annotations as `<section type="annotation">` blocks; this helper is
 * what makes them appear as inline yellow underlines + tooltips on the
 * passage they were anchored to.
 *
 * Char-coord mapping mirrors the server's materialized-text format:
 *  - paragraph texts concatenated by `\n` (one char per block boundary)
 *  - first paragraph contributes no leading newline
 * This is the same convention `mapCharToPm` in AnnotationHighlight.ts
 * and `charPosFromPmPos` in AnnotationOverlay.tsx use, so the
 * resulting start_pos/end_pos round-trip cleanly into PM positions.
 */

import type { AnnotationData, AnnotationReply } from './AnnotationHighlight'

interface StructuredParagraph {
  index: number
  text: string
  author_id?: string | null
  author_name?: string | null
  author_color?: string | null
  is_heading?: boolean
  heading_level?: number | null
  block_id?: string | null
  section_id?: string | null
  section_type?: 'product' | 'question' | 'task' | 'annotation' | null
  section_role?: 'start' | 'content' | 'reply' | 'end' | null
  section_attrs?: Record<string, unknown> | null
  created_at?: string | null
}

const SECTION_TAG_RE = /^\s*<\/?(section|reply|main)\b[^>]*>\s*$/i

export function annotationsFromSections(
  paragraphs: StructuredParagraph[],
): AnnotationData[] {
  if (!paragraphs || paragraphs.length === 0) return []

  // Build block_id → char prefix sum so we can resolve anchors back to
  // global char positions for the inline-decoration plugin.
  // paragraphs include the literal <section …> / </section> tag lines —
  // they DO take up char space in the materialized text just like any
  // other line, so we count them too. (`textBetween('\n')` skips XML
  // attrs but keeps line-boundaries; safer to include in counts.)
  const charStartByIndex: number[] = []
  let running = 0
  for (let i = 0; i < paragraphs.length; i++) {
    if (i > 0) running += 1 // newline between blocks
    charStartByIndex[i] = running
    running += (paragraphs[i].text || '').length
  }
  const blockIdToIndex = new Map<string, number>()
  for (const p of paragraphs) {
    if (p.block_id) blockIdToIndex.set(p.block_id, p.index)
  }

  // Group by section_id (only annotation type).
  const groups = new Map<string, StructuredParagraph[]>()
  for (const p of paragraphs) {
    if (p.section_type !== 'annotation' || !p.section_id) continue
    const arr = groups.get(p.section_id) || []
    arr.push(p)
    groups.set(p.section_id, arr)
  }

  const out: AnnotationData[] = []
  for (const [sid, paras] of groups) {
    const startPara = paras.find((p) => p.section_role === 'start')
    const attrs = (startPara?.section_attrs ||
      paras[0]?.section_attrs ||
      {}) as Record<string, unknown>

    const anchorParagraphId =
      (attrs.anchor_paragraph_id as string | undefined) || null
    const anchorStart = parseInt(
      String(attrs.anchor_start ?? '0'),
      10,
    )
    const anchorEnd = parseInt(String(attrs.anchor_end ?? '0'), 10)
    const anchorQuote = (attrs.anchor_quote as string | undefined) || ''
    const initiator = (attrs.initiator as string | undefined) || 'Unknown'

    // Build the note body AND the reply thread. The body is every
    // paragraph in the section that ISN'T a tag line and isn't inside
    // a <reply>. Replies are collected separately so the tooltip can
    // surface them inline (2026-06-12) instead of forcing the user
    // to open the full SectionRenderer card.
    let inReply = false
    let currentReplyAttrs: Record<string, unknown> | null = null
    let currentReplyLines: string[] = []
    const bodyLines: string[] = []
    const replies: AnnotationReply[] = []
    const flushReply = () => {
      const replyText = currentReplyLines.join('\n').trim()
      if (replyText) {
        const a = currentReplyAttrs || {}
        replies.push({
          text: replyText,
          author_id:
            (a.author_id as string | undefined) ||
            (a.by as string | undefined) ||
            null,
          author_name:
            (a.author_name as string | undefined) ||
            (a.by as string | undefined) ||
            null,
          created_at: (a.created_at as string | undefined) || null,
        })
      }
      currentReplyLines = []
      currentReplyAttrs = null
    }
    for (const p of paras) {
      const t = (p.text || '').trim()
      if (/^<reply\b/i.test(t)) {
        inReply = true
        currentReplyAttrs = p.section_attrs || null
        continue
      }
      if (/^<\/reply>/i.test(t)) {
        flushReply()
        inReply = false
        continue
      }
      if (inReply || p.section_role === 'reply') {
        // Reply paragraph contributes to the current reply being built.
        if (!SECTION_TAG_RE.test(t)) currentReplyLines.push(p.text || '')
        if (!currentReplyAttrs && p.section_attrs) {
          currentReplyAttrs = p.section_attrs
        }
        continue
      }
      if (SECTION_TAG_RE.test(t)) continue
      bodyLines.push(p.text || '')
    }
    if (inReply) flushReply() // tolerate unclosed <reply>
    // Die Notiz wird als `<main>…</main>` in EINEM Absatz gespeichert —
    // die Tag-Zeilen-Regel oben greift dann nicht. Hülle hier abstreifen,
    // sonst steht `<main>` in Karte und Tooltip (Randspalte 2026-10-02).
    const noteText = bodyLines
      .join('\n\n')
      .replace(/^\s*<main>\s*/i, '')
      .replace(/\s*<\/main>\s*$/i, '')
      .trim()

    // Resolve anchor → global char positions.
    let startPos = 0
    let endPos = 0
    if (anchorParagraphId && blockIdToIndex.has(anchorParagraphId)) {
      const idx = blockIdToIndex.get(anchorParagraphId)!
      const base = charStartByIndex[idx] ?? 0
      startPos = base + (Number.isFinite(anchorStart) ? anchorStart : 0)
      endPos = base + (Number.isFinite(anchorEnd) ? anchorEnd : 0)
    } else if (anchorQuote) {
      // Anchor paragraph drifted (e.g. block rewritten) — leave
      // start_pos/end_pos zero so AnnotationHighlight falls back to
      // snippet search via the `snippet` field.
      startPos = 0
      endPos = 0
    }

    // Author from start paragraph; section_attrs.initiator is a string
    // label, not an id, so we keep the paragraph author for color.
    const author_id =
      (startPara?.author_id as string | undefined) || null
    const author_name =
      (startPara?.author_name as string | undefined) || initiator || 'Unknown'
    const created_at =
      (startPara?.created_at as string | undefined) || null

    out.push({
      id: sid,
      start_pos: startPos,
      end_pos: endPos,
      note_text: noteText,
      author_id,
      author_name,
      created_at,
      snippet: anchorQuote || undefined,
      source: 'section',
      replies: replies.length > 0 ? replies : undefined,
    })
  }

  return out
}
